// Installments API — connected directly to FastAPI backend, strictly zero mock data
import api from '../../../../api/client'

/** Build string option arrays from a masters lookup, prefixed with 'Select'. */
function toStringOptions(values, defaults = []) {
  const valid = Array.isArray(values) ? values.filter(Boolean) : []
  const list = valid.length > 0 ? valid : (defaults || [])
  return ['Select', ...Array.from(new Set(list))]
}

export let COURSE_OPTIONS = ['Select']
export let BATCH_OPTIONS = ['Select']
export let COMPANY_OPTIONS = ['Select']
export let BRANCH_OPTIONS = ['Select']
export let SCHEME_OPTIONS = ['Select']
export let ACADEMIC_YEAR_OPTIONS = ['Select']
export let SUBJECT_OPTIONS = ['Select']

export const GENDER_OPTIONS = ['Select', 'Male', 'Female', 'Others']
export const STATUS_OPTIONS = ['All', 'Paid', 'Unpaid', 'Partial']
export const PAYMENT_STATUS_OPTIONS = ['All', 'Paid', 'Pending', 'Overdue', 'Partial']

export const DATE_PERIOD_OPTIONS = [
  { value: 'period', label: 'Period' },
  { value: 'current_year', label: 'Current Year' },
  { value: 'current_month', label: 'Current Month' },
  { value: 'current_week', label: 'Current Week' },
  { value: 'last_year', label: 'Last Year' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'last_week', label: 'Last Week' },
  { value: 'next_year', label: 'Next Year' },
  { value: 'next_month', label: 'Next Month' },
  { value: 'next_week', label: 'Next Week' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
]

export const ALERT_TIMING_OPTIONS = [
  { value: -1, label: 'None' },
  { value: 0, label: 'On due date' },
  { value: 1, label: '1 day before due date' },
  { value: 2, label: '2 days before due date' },
  { value: 3, label: '3 days before due date' },
  { value: 4, label: '4 days before due date' },
  { value: 5, label: '5 days before due date' },
  { value: 6, label: '6 days before due date' },
  { value: 7, label: '7 days before due date' },
  { value: 10, label: '10 days before due date' },
  { value: 15, label: '15 days before due date' },
]

async function fetchOptionList(lookup) {
  try {
    const { data } = await api.get(`/admission/masters/${lookup}`)
    return Array.isArray(data) ? data : (Array.isArray(data?.options) ? data.options : [])
  } catch {
    return []
  }
}

export async function loadInstallmentOptions() {
  try {
    const [courses, batches, institutes, branches, schemes, years, subjects] = await Promise.all([
      fetchOptionList('courses'),
      fetchOptionList('batches'),
      fetchOptionList('institutes'),
      fetchOptionList('branches'),
      fetchOptionList('schemes'),
      fetchOptionList('academic_years'),
      fetchOptionList('subjects'),
    ])
    COURSE_OPTIONS = toStringOptions(courses)
    BATCH_OPTIONS = toStringOptions(batches)
    COMPANY_OPTIONS = toStringOptions(institutes)
    BRANCH_OPTIONS = toStringOptions(branches)
    SCHEME_OPTIONS = toStringOptions(schemes)
    ACADEMIC_YEAR_OPTIONS = toStringOptions(years)
    SUBJECT_OPTIONS = toStringOptions(subjects)
  } catch (err) {
    console.error('Failed to load installment master options:', err)
  }
}

loadInstallmentOptions()

export async function listInstallments(filters = {}) {
  const params = {
    companyFilter: filters.companyFilter === 'Select' ? '' : filters.companyFilter,
    branchFilter: filters.branchFilter === 'Select' ? '' : filters.branchFilter,
    courseFilter: filters.courseFilter === 'Select' ? '' : filters.courseFilter,
    schemeFilter: filters.schemeFilter === 'Select' ? '' : filters.schemeFilter,
    academicYearFilter: filters.academicYearFilter === 'Select' ? '' : filters.academicYearFilter,
    batchFilter: filters.batchFilter === 'Select' ? '' : filters.batchFilter,
    subjectFilter: filters.subjectFilter === 'Select' ? '' : filters.subjectFilter,
    genderFilter: filters.genderFilter === 'Select' ? '' : filters.genderFilter,
    statusFilter: filters.statusFilter === 'All' ? '' : filters.statusFilter,
    paymentStatusFilter: filters.paymentStatusFilter === 'All' ? '' : filters.paymentStatusFilter,
    searchField: filters.searchField || 'Name',
    searchText: filters.searchText || '',
    fromDate: filters.fromDate || '',
    toDate: filters.toDate || '',
    tab: filters.tab || 'active',
  }

  const { data } = await api.get('/admission/installments', { params })
  return {
    data: data?.data || [],
    total: data?.total || 0,
    summary: data?.summary || null,
  }
}

export async function getInstallmentsSummary(filters = {}) {
  const params = {
    companyFilter: filters.companyFilter === 'Select' ? '' : filters.companyFilter,
    branchFilter: filters.branchFilter === 'Select' ? '' : filters.branchFilter,
    courseFilter: filters.courseFilter === 'Select' ? '' : filters.courseFilter,
    schemeFilter: filters.schemeFilter === 'Select' ? '' : filters.schemeFilter,
    batchFilter: filters.batchFilter === 'Select' ? '' : filters.batchFilter,
    genderFilter: filters.genderFilter === 'Select' ? '' : filters.genderFilter,
    statusFilter: filters.statusFilter === 'All' ? '' : filters.statusFilter,
    paymentStatusFilter: filters.paymentStatusFilter === 'All' ? '' : filters.paymentStatusFilter,
    fromDate: filters.fromDate || '',
    toDate: filters.toDate || '',
    tab: filters.tab || 'active',
  }

  const { data } = await api.get('/admission/installments/summary', { params })
  return {
    total: Number(data?.total || 0),
    paid: Number(data?.paid || 0),
    balance: Number(data?.balance || 0),
    overdue: Number(data?.overdue || 0),
  }
}

export async function getInstallment(id) {
  const { data } = await api.get(`/admission/installments/${id}`)
  return data
}

export async function createInstallment(payload) {
  const { data } = await api.post('/admission/installments', payload)
  return data?.data || data
}

export async function updateInstallment(id, payload) {
  const { data } = await api.put(`/admission/installments/${id}`, payload)
  return data?.data || data
}

export async function deleteInstallment(id) {
  const { data } = await api.delete(`/admission/installments/${id}`)
  return data
}

export async function archiveInstallment(id) {
  const { data } = await api.patch(`/admission/installments/${id}/archive`)
  return data
}

export async function getInstallmentAlertSettings() {
  const { data } = await api.get('/admission/installments/settings/alerts')
  return data
}

export async function saveInstallmentAlertSettings(settings) {
  const { data } = await api.post('/admission/installments/settings/alerts', settings)
  return data
}
